import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaShoppingCart, FaStar, FaChevronLeft, FaCheck, FaShieldAlt, FaDownload, FaSyncAlt } from 'react-icons/fa';
import { api } from '../services/api';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useLocale } from '../context/LocaleContext';
import { prepareRichTextForDisplay, richTextToPlainText } from '../utils/richText';
import ProductReviews from '../components/ProductReviews';
import './ProductDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const toPriceNumber = (value) => typeof value === 'number' ? value : parseInt(String(value || '').replace(/,/g, ''), 10) || 0;

const TAB_KEYS = ['intro', 'reviews', 'faq', 'changelog'];
const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const { t } = useTranslation();
  const { locale, localePath, formatDate, formatNumber, setPageAlternates } = useLocale();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => (
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab') === 'reviews'
      ? 'reviews'
      : 'intro'
  ));
  const recordedViewIds = useRef(new Set());

  const handleReviewSummaryChange = useCallback((summary) => {
    setProduct((current) => current ? {
      ...current,
      rating: summary.total ? summary.average : null,
      reviewsCount: summary.total || 0,
    } : current);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/api/products/${id}?locale=${encodeURIComponent(locale.code)}`);
        if (res.success && res.data) {
          const p = res.data;
          const productId = p._id || p.id;
          const rawDetailImageUrl = p.detailImageUrl || p.imageUrl || '';
          const detailImageUrl = rawDetailImageUrl.startsWith('/') ? `${API_URL}${rawDetailImageUrl}` : rawDetailImageUrl;
          setProduct({ ...p, id: p._id || p.id, detailImageUrl });
          setPageAlternates(Object.fromEntries(
            Object.entries(p.alternates || {}).map(([code, slug]) => [
              code,
              `/${code}/products/${slug}`,
            ])
          ));

          if (!recordedViewIds.current.has(productId)) {
            recordedViewIds.current.add(productId);
            api.post(`/api/products/${productId}/view`, {}).catch(() => {
              recordedViewIds.current.delete(productId);
            });
          }
          if (p.slug && id !== p.slug) {
            navigate(localePath(`/products/${p.slug}`), { replace: true });
          }
        } else {
          setProduct(null);
        }
      } catch (_) {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, locale.code, localePath, navigate, setPageAlternates]);

  useEffect(() => {
    if (!product?.name) return undefined;
    const siteTitle = locale.code === 'fa' ? (settings.general.siteTitle || t('home.title')) : t('home.title');
    const previousTitle = document.title;
    let meta = document.querySelector('meta[name="description"]');
    const createdMeta = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute('content') || '';
    document.title = `${product.seoTitle || product.name} | ${siteTitle}`;
    meta.setAttribute('content', product.seoDescription || richTextToPlainText(product.description).slice(0, 160));
    return () => {
      document.title = previousTitle;
      if (createdMeta) meta.remove();
      else meta.setAttribute('content', previousDescription);
    };
  }, [locale.code, product, settings.general.siteTitle, t]);

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <p className="product-detail-loading">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="product-not-found">
            <h2>{t('productDetail.missing')}</h2>
            <Link to={localePath('/products')} className="product-detail-back-btn">{t('productDetail.back')}</Link>
          </div>
        </div>
      </div>
    );
  }

  const regularPrice = toPriceNumber(product.price);
  const priceAvailable = product.priceAvailable !== false;
  const purchaseAvailable = priceAvailable && Boolean(product.paymentEnabled);
  const currencyLabel = product.currencyLabel || locale.currencyLabel;
  const discountPrice = toPriceNumber(product.discountPrice);
  const hasDiscount = discountPrice > 0 && discountPrice < regularPrice;
  const effectivePrice = hasDiscount ? discountPrice : regularPrice;
  const discountPercent = hasDiscount ? Math.round((1 - discountPrice / regularPrice) * 100) : 0;
  const version = product.version || '—';
  const releaseDate = formatDate(product.releaseDate || product.createdAt);
  const updatedAt = formatDate(product.lastUpdated || product.updatedAt || product.releaseDate);
  const faq = Array.isArray(product.faq) ? product.faq : [];
  const changelog = Array.isArray(product.changelog) ? [...product.changelog].reverse() : [];
  const descriptionHtml = prepareRichTextForDisplay(product.description, API_URL);

  return (
    <div className="product-detail-page">
      <div className="product-detail-shell">
        <Link to={localePath('/products')} className="product-detail-breadcrumb">
          <FaChevronLeft /> {t('productDetail.back')}
        </Link>
      </div>

      <section className="product-header product-detail-shell">
        <div className="product-header-left">
          <div className="product-header-banner">
            {product.detailImageUrl ? (
              <img src={product.detailImageUrl} alt={product.name} />
            ) : (
              <div className="product-header-banner-placeholder">{product.image || '📦'}</div>
            )}
          </div>
          <div className="product-title-block">
            <span className="product-detail-eyebrow">{product.category || t('productDetail.digital')}</span>
            <h1 className="product-detail-title">{product.name}</h1>
            <p>{t('productDetail.tagline')}</p>
          </div>
        </div>
        <div className="product-header-features">
          <span className="product-purchase-kicker">{t('productDetail.purchase')}</span>
          {product.rating && (
            <div className="product-detail-rating">
              <FaStar className="product-detail-star" />
              <span>{formatNumber(product.rating)}</span>
              {product.reviewsCount > 0 && <small>({t('productDetail.reviewsCount', { count: formatNumber(product.reviewsCount) })})</small>}
            </div>
          )}
          <ul className="product-header-features-list">
            <li><FaShieldAlt /> {t('productDetail.secure')}</li>
            <li><FaDownload /> {t('productDetail.access')}</li>
            <li><FaSyncAlt /> {t('productDetail.updates')}</li>
            {Array.isArray(product.features) && product.features.map((f, i) => (
              <li key={i}><FaCheck /> {f}</li>
            ))}
          </ul>
          <div className={`product-header-price ${hasDiscount ? 'has-discount' : ''}`}>
            {priceAvailable && hasDiscount && (
              <div className="product-detail-discount-row">
                <del>{formatNumber(regularPrice)} {currencyLabel}</del>
                <span>{t('products.discount', { count: formatNumber(discountPercent) })}</span>
              </div>
            )}
            <strong>{priceAvailable ? `${formatNumber(effectivePrice)} ${currencyLabel}` : t('products.priceUnavailable')}</strong>
          </div>
          <button type="button" className="product-header-buy-btn" disabled={!purchaseAvailable} onClick={() => navigate(localePath(`/checkout/${product._id || product.id}`))}>
            <FaShoppingCart /> {purchaseAvailable ? t('productDetail.buy') : t('products.paymentUnavailable')}
          </button>
        </div>
      </section>

      {/* product-body: 80% تب‌ها، 20% سایدبار */}
      <section className="product-body product-detail-shell">
        <div className="product-body-main">
          <div className="product-tabs">
            {TAB_KEYS.map(key => (
              <button
                key={key}
                type="button"
                className={`product-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {t(`productDetail.${key}`)}
              </button>
            ))}
          </div>
          <div className="product-tab-content">
            {activeTab === 'intro' && (
              <div className="product-tab-panel">
                <h3>{t('productDetail.introTitle')}</h3>
                <div
                  className="product-intro-text"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              </div>
            )}
            {activeTab === 'reviews' && (
              <div className="product-tab-panel">
                <ProductReviews
                  productId={product._id || product.id}
                  productSlug={product.slug}
                  productName={product.name}
                  onSummaryChange={handleReviewSummaryChange}
                />
              </div>
            )}
            {activeTab === 'faq' && (
              <div className="product-tab-panel">
                <h3>{t('productDetail.faq')}</h3>
                {faq.length > 0 ? (
                  <ul className="product-faq-list">
                    {faq.map((item, index) => (
                      <li key={item._id || item.id || index} className="product-faq-item">
                        <strong>{item.q}</strong>
                        <p>{item.a}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="product-tab-empty">{t('productDetail.faqEmpty')}</p>
                )}
              </div>
            )}
            {activeTab === 'changelog' && (
              <div className="product-tab-panel">
                <h3>{t('productDetail.changelog')}</h3>
                {changelog.length > 0 ? (
                  <ul className="product-changelog-list">
                    {changelog.map((entry, i) => (
                      <li key={entry._id || i} className="product-changelog-item">
                        <div className="product-changelog-version">
                          {t('productDetail.version', { version: entry.version || '—' })} {entry.date ? `— ${formatDate(entry.date)}` : ''}
                        </div>
                        {Array.isArray(entry.items) && entry.items.length > 0 && (
                          <ul>
                            {entry.items.map((item, j) => <li key={j}>{item}</li>)}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="product-tab-empty">{t('productDetail.changelogEmpty')}</p>
                )}
              </div>
            )}
          </div>
        </div>
        <aside className="product-body-sidebar">
          <div className="product-sidebar-card">
            <div className="product-sidebar-row">
              <span className="product-sidebar-label">{t('productDetail.productVersion')}</span>
              <span className="product-sidebar-value">{version}</span>
            </div>
            <div className="product-sidebar-row">
              <span className="product-sidebar-label">{t('productDetail.updatedAt')}</span>
              <span className="product-sidebar-value">{updatedAt}</span>
            </div>
            <div className="product-sidebar-row">
              <span className="product-sidebar-label">{t('productDetail.releasedAt')}</span>
              <span className="product-sidebar-value">{releaseDate}</span>
            </div>
            <div className="product-sidebar-row">
              <span className="product-sidebar-label">{t('productDetail.category')}</span>
              <span className="product-sidebar-value">{product.category || '—'}</span>
            </div>
            <div className={`product-sidebar-price ${hasDiscount ? 'has-discount' : ''}`}>
              {priceAvailable && hasDiscount && (
                <div className="product-detail-discount-row">
                  <del>{formatNumber(regularPrice)} {currencyLabel}</del>
                  <span>{t('products.discount', { count: formatNumber(discountPercent) })}</span>
                </div>
              )}
              <strong>{priceAvailable ? `${formatNumber(effectivePrice)} ${currencyLabel}` : t('products.priceUnavailable')}</strong>
            </div>
            <button type="button" className="product-sidebar-buy-btn" disabled={!purchaseAvailable} onClick={() => navigate(localePath(`/checkout/${product._id || product.id}`))}>
              <FaShoppingCart /> {purchaseAvailable ? t('productDetail.buy') : t('products.paymentUnavailable')}
            </button>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default ProductDetail;
